import {
    List,
    Datagrid,
    TextField,
    BooleanField,
    DateField,
    EditButton,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    BooleanInput,
    required,
    useRecordContext,
    useNotify,
    useRefresh,
    TopToolbar,
    FilterButton,
    CreateButton,
    ExportButton,
    SearchInput,
    NumberField,
} from 'react-admin';
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { getBlogImageEndpoint } from '../config';

// Simple Markdown TextArea Input
const MarkdownInput = ({ source, label, validate, rows = 6, ...props }) => (
    <TextInput
        source={source}
        label={label}
        validate={validate}
        multiline
        rows={rows}
        fullWidth
        helperText="Supports Markdown: **bold**, *italic*, # headings, - lists, [links](url)"
        {...props}
    />
);

// Custom component for blog cover image upload
const BlogCoverImageUpload = ({ label }) => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (file) => {
        if (!file || !record?.id) return;
        
        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                getBlogImageEndpoint(record.id),
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );
            
            if (!response.ok) {
                throw new Error('Upload failed');
            }
            
            const data = await response.json();
            notify('Cover image uploaded successfully', { type: 'success' });
            refresh();
        } catch (error) {
            notify('Failed to upload cover image', { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const currentImageUrl = record?.cover_image;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                {label}
            </Typography>
            {currentImageUrl && (
                <Box sx={{ mb: 1 }}>
                    <img 
                        src={currentImageUrl} 
                        alt="Blog cover"
                        style={{ 
                            maxWidth: '400px', 
                            maxHeight: '200px',
                            objectFit: 'cover',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }} 
                    />
                </Box>
            )}
            <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file);
                }}
                disabled={uploading}
                style={{ marginBottom: '8px' }}
            />
            {uploading && <Typography variant="caption">Uploading...</Typography>}
        </Box>
    );
};

const BlogFilters = [
    <SearchInput source="q" alwaysOn key="search" />,
];

const BlogListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ExportButton />
    </TopToolbar>
);

export const BlogList = () => (
    <List filters={BlogFilters} actions={<BlogListActions />}>
        <Datagrid>
            <TextField source="id" />
            <TextField source="title" />
            <BooleanField source="is_published" label="Published" />
            <NumberField source="view_count" label="Views" />
            <DateField source="CreatedAt" />
            <DateField source="UpdatedAt" />
            <EditButton />
        </Datagrid>
    </List>
);

export const BlogEdit = () => (
    <Edit>
        <SimpleForm>
            <TextInput source="title" validate={[required()]} fullWidth />
            <TextInput source="slug" fullWidth />
            <MarkdownInput source="content" label="Content (Markdown)" validate={[required()]} />
            <BooleanInput source="is_published" label="Published" />
            <BlogCoverImageUpload label="Cover Image" />
        </SimpleForm>
    </Edit>
);

export const BlogCreate = () => (
    <Create>
        <SimpleForm>
            <TextInput source="title" validate={[required()]} fullWidth />
            <TextInput source="slug" fullWidth />
            <MarkdownInput source="content" label="Content (Markdown)" validate={[required()]} />
            <BooleanInput source="is_published" label="Published" defaultValue={false} />
        </SimpleForm>
    </Create>
); 